// TODO - Documentar
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as con from "../../GlobalConstants"
import { filterObjectByKeys } from '../../GlobalFunctions';
import { checkServer, fetchMarketAssets, fetchSurrogateUserList, setApplicationParameter } from '../../store/slices/applicationParametersSlice';
import { fetchAllMarketData,fetchInitialTRM, updateSpot } from '../../store/slices/marketValuesSlice';
import { fetchUserLines } from '../../store/slices/userLinesSlice';
import { fetchUserAttributes } from '../../store/slices/userProfileSlice';
import { fetchAdvisors } from '../../store/slices/advisorsSlice';
import { fetchAllLineData, fetchMarketAlerts, resetTransactions } from '../../store/slices/transactionsSlice';
import { fetchModelParameters } from '../../store/slices/modelParametersSlice';
import { fetchScenarios } from '../../store/slices/scenariosSlice';
import { fetchUserLineApplicationParameters } from '../../store/slices/userLineApplicationParametersSlice';
import { parseDate } from '../../utils/dateFunctions';

const DATE_PARAMETERS = new Set([
  con.FORWARD_ASSESSMENT_REFERENCE_DATE,
  con.FORWARD_ASSESSMENT_START_DATE,
  con.OPTION_ASSESSMENT_REFERENCE_DATE,
  con.OPTION_ASSESSMENT_START_DATE,
  con.CURRENT_DATE
])

/**
 * Hook for a single application parameter in the store read only
 */
export function RefApplicationParameterHook(parameterName) {
  const isDate = DATE_PARAMETERS.has(parameterName)
  const raw = useSelector(state => state[con.REDUCER_APPLICATION_PARAMETERS][parameterName])
  return useMemo(() => isDate ? parseDate(raw) : raw, [isDate, raw])
}

/**
 * Hook for a single application parameter in the store (read & write)
 */
export function StateApplicationParameterHook(parameterName) {
  const dispatch = useDispatch();
  const isDate = DATE_PARAMETERS.has(parameterName)

  const raw = useSelector(
    (state) => state[con.REDUCER_APPLICATION_PARAMETERS][parameterName]
  );

  const value = useMemo(() => isDate ? parseDate(raw) : raw, [isDate, raw])

  const setParameter = (newVal) =>
    dispatch(
      setApplicationParameter({
        id: parameterName,
        value: isDate ? newVal.toISO() : newVal,
      })
    );

  return [value, setParameter];
}

/**
 * Hook for a single application parameter in the store. Only write
 */
export function EditApplicationParameterHook(parameterName) {
  const dispatch = useDispatch();
  const isDate = DATE_PARAMETERS.has(parameterName)

  const setParameter = (newVal) =>
    dispatch(
      setApplicationParameter({
        id: parameterName,
        value: isDate ? newVal.toISO() : newVal,
      })
    );

  return setParameter;
}

/**
 * Hook for all application parameter in the store 
 */
 export function AllApplicationParametersHook() {

    const value = useSelector(state => state[con.REDUCER_APPLICATION_PARAMETERS])


    return(value);
}

/**
 * Hook for a single nested application parameter in the store read only
 */
 export function RefNestedApplicationParameterHook(parameterName, attributeName) {

    const value = useSelector(state => state[con.REDUCER_APPLICATION_PARAMETERS][parameterName][attributeName])

    return(value);
}



/**
 * Hook for a loading parameter
 */
 export function RefApplicationLoadingParameterHook(loadingParameterName) {

    const value = useSelector(state => state[con.REDUCER_APPLICATION_PARAMETERS])

    return(value);
}


/**
 * Hook for a single nested application parameter in the store
 */
export function SetNestedApplicationParameterHook(parameterName, attributeName) {
  const dispatch = useDispatch();

  const value = useSelector(
    (state) => state[con.REDUCER_APPLICATION_PARAMETERS][parameterName]
  );

  const setValue = (newValue) => {
    dispatch(
      setApplicationParameter({
        id: parameterName,
        value: {
          ...value,
          [attributeName]: newValue,
        },
      })
    );
  };

  return setValue;
}


/**
 * Hook for a single nested application parameter in the store
 */
export function StateNestedApplicationParameterHook(parameterName, attributeName) {
  const dispatch = useDispatch();

  const allValues = useSelector(
    (state) => state[con.REDUCER_APPLICATION_PARAMETERS][parameterName]
  );

  const value = allValues?.[attributeName];

  const setValue = (newValue) => {
    dispatch(
      setApplicationParameter({
        id: parameterName,
        value: {
          ...allValues,
          [attributeName]: newValue,
        },
      })
    );
  };

  return [value, setValue];
}


/**
 * Hook for Application Parameters used in model
 */
export function RefModelApplicationParameterHook()
{
    
    const applicationParameters = AllApplicationParametersHook()

    const [modelApplicationParameters, setModelApplicationParameters] = useState(() => filterObjectByKeys(applicationParameters, con.MODEL_APPLICATION_PARAMETERS))
    
    const lastValue = useRef(() => modelApplicationParameters)

    useEffect(() => {

        let newValues = filterObjectByKeys(applicationParameters, con.MODEL_APPLICATION_PARAMETERS)

        if(JSON.stringify(newValues) !== JSON.stringify(lastValue.current))
        {
            setModelApplicationParameters(newValues)
            lastValue.current = newValues

        }
            

    }, [applicationParameters])
    

    return(modelApplicationParameters)

}


/**
 * Hook to determine if the current viewport should be considered mobile.
 *
 * - Uses window dimensions
 * - Listens to resize events
 * - Optimized to avoid unnecessary re-renders
 *
 * @returns {boolean} isMobile
 */
export const useIsMobile = () => {
  const getIsMobile = () =>
    window.innerWidth <= con.MOBILE_MAX_WIDTH ||
    window.innerHeight <= con.MOBILE_MAX_HEIGHT;

  const [isMobile, setIsMobile] = useState(null);

  useEffect(() => {
    const update = () => setIsMobile(getIsMobile());

    update();

    window.addEventListener("resize", update);

    return () => window.removeEventListener("resize", update);
  }, []);

  return isMobile ?? false;
};

export const useCheckServer = () => {
  const dispatch = useDispatch();
  const serverStatus = RefNestedApplicationParameterHook(con.SERVER, con.STATUS)

  useEffect(() => {
    if (serverStatus === con.NOT_STARTED) {
      dispatch(checkServer());
    }
  }, [dispatch, serverStatus]);
};

/**
 * Initializes core application data.
 *
 * This hook centralizes all bootstrap API calls required
 * when the app becomes ready.
 *
 * @param {boolean} appReady - Indicates if the app is ready to initialize
 * @param {boolean} isStaff - Indicates if the current user is staff * 
 */
export const useInitializeApp = (appReady, isStaff) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!appReady) return;

    const initialize = async () => {
      try {
        await Promise.all([
          dispatch(updateSpot()),
          dispatch(fetchUserLines()),
          dispatch(fetchUserAttributes()),
          dispatch(fetchAdvisors()),
          dispatch(fetchInitialTRM()),
          dispatch(fetchAllMarketData()),    
          dispatch(fetchScenarios()),   
        ]);

        if (isStaff) {
          dispatch(fetchSurrogateUserList())
        }

      } catch (err) {
        console.error("App initialization failed:", err);
      }
    };

    initialize();
  }, [appReady, isStaff, dispatch]);
};


export const useOperationalContext = (
  appReady,
  isStaff,
  surrogateUser,
  selectedUserLine
) => {
  const dispatch = useDispatch();

  // Surrogate switch
  useEffect(() => {
    if (!appReady || !isStaff || surrogateUser == null) return;

    dispatch(resetTransactions());
    dispatch(fetchUserAttributes());
    dispatch(fetchUserLines());

  }, [appReady, isStaff, surrogateUser, dispatch]);

  // Line switch
  useEffect(() => {
    if (!appReady || selectedUserLine == null) return;

    dispatch(fetchModelParameters());
    dispatch(fetchUserLineApplicationParameters());
    dispatch(fetchAllLineData());
    dispatch(fetchMarketAlerts());
    dispatch(fetchMarketAssets());
    

  }, [appReady, selectedUserLine, dispatch]);
};